import { FireIcon, SparklesIcon } from '@heroicons/react/20/solid';
import InfoTooltip from 'components/layout/info-tooltip';
import Tooltip from 'components/layout/tooltip';
import { SystemOptionType } from 'data/types';
import { formatCoverageScore } from 'utils/coverage-score-format';
import {
  DuctedCoverageBasis,
  ductedCoverageTooltipText,
  ductedPercentLabel
} from 'utils/ducted-coverage';

type CoverageIndicatorProps =
  | {
      type: 'heating' | 'cooling';
      score: number | null;
      insufficientTimePercent: number;
      minimumPercent: number;
      condenserCoverage?: SystemOptionType['condenserCoverage'];
    }
  | {
      type: 'heating' | 'cooling';
      score: number | null;
      insufficientTimePercent?: number;
      minimumPercent?: number;
      condenserCoverage: SystemOptionType['condenserCoverage'];
    }
  // AHA-6796 ducted design-load variant: value is the coverage percent
  // (null = not computable, renders "—") and the info bubble always shows
  // the design-load explainer for the basis.
  | {
      type: 'heating' | 'cooling';
      ducted: { value: number | null; basis: DuctedCoverageBasis };
    };

function DuctedCoverageIndicator({
  type,
  ducted
}: {
  type: 'heating' | 'cooling';
  ducted: { value: number | null; basis: DuctedCoverageBasis };
}) {
  return (
    <div className='relative flex items-center gap-1'>
      {type === 'heating' ? (
        <FireIcon className='h-5 w-5 text-orange-500' />
      ) : (
        <SparklesIcon className='h-5 w-5 text-sky-500' />
      )}
      <span>{ductedPercentLabel(ducted.value)}</span>
      <InfoTooltip>
        <div className='max-w-[280px] text-center'>
          {ductedCoverageTooltipText(type, ducted)}
        </div>
      </InfoTooltip>
    </div>
  );
}

export default function CoverageIndicator(props: CoverageIndicatorProps) {
  if ('ducted' in props) {
    const { type, ducted } = props;
    return <DuctedCoverageIndicator type={type} ducted={ducted} />;
  }

  const {
    type,
    score,
    insufficientTimePercent,
    minimumPercent,
    condenserCoverage
  } = props;

  return (
    <div
      className={`relative flex items-center gap-1 ${
        score != null && score < 100 ? 'cursor-help' : ''
      }`}
    >
      {type === 'heating' ? (
        <FireIcon className='h-5 w-5 text-orange-500' />
      ) : (
        <SparklesIcon className='h-5 w-5 text-sky-500' />
      )}
      <span>{formatCoverageScore(score)}</span>
      {score != null && score < 100 && type === 'heating' && (
        <Tooltip>
          {condenserCoverage ? (
            condenserCoverage
              .filter(
                (condenser) =>
                  (condenser.percentageOfTimeOfInsufficientHeating ?? 0) > 0
              )
              .map((condenser) => (
                <div key={condenser.uniqueId} className='text-center'>
                  {condenserCoverage.length > 1 && `${condenser.name}: `}
                  insufficient{' '}
                  {condenser.percentageOfTimeOfInsufficientHeating ?? 0}% of the
                  time, averaging{' '}
                  {Math.floor(condenser.avgPartialHeatingCoveragePercent ?? 0)}%
                  coverage during that time
                </div>
              ))
          ) : (
            <div>
              insufficient {insufficientTimePercent ?? 0}% of the time,
              averaging {Math.floor(minimumPercent ?? 0)}% coverage during that
              time
            </div>
          )}
        </Tooltip>
      )}
      {score != null && score < 100 && type === 'cooling' && (
        <Tooltip>
          {condenserCoverage ? (
            condenserCoverage
              .filter(
                (condenser) =>
                  (condenser.percentageOfTimeOfInsufficientCooling ?? 0) > 0
              )
              .map((condenser) => (
                <div key={condenser.uniqueId} className='text-center'>
                  {condenserCoverage.length > 1 && `${condenser.name}: `}
                  insufficient{' '}
                  {condenser.percentageOfTimeOfInsufficientCooling ?? 0}% of the
                  time, averaging{' '}
                  {Math.floor(condenser.avgPartialCoolingCoveragePercent ?? 0)}%
                  coverage during that time
                </div>
              ))
          ) : (
            <div>
              insufficient {insufficientTimePercent ?? 0}% of the time,
              averaging {Math.floor(minimumPercent ?? 0)}% coverage during that
              time
            </div>
          )}
        </Tooltip>
      )}
    </div>
  );
}
